/**
 * AI Engine — heuristiques pures (analyzeContext, predictions, score).
 *
 * Port direct des fonctions `analyzeContext`, `_isCustodyTransition`,
 * `generatePredictions`, `getSituationScore`, `getSituationLabel` du
 * proto (l. 1104-1252). Aucun side effect (sauf `detectPatterns` qui
 * passe par le module `memory`).
 *
 * Ces fonctions sont 100 % testables — pas d'appel à `Date` interne :
 * la date courante est injectable via le paramètre `now` (défaut
 * `new Date()`).
 */
import type { AiBagItem, AiContext, AiInputData, AiMemory, Prediction } from './types.js';
import { saveMemory } from './memory.js';
import { t } from '../../i18n/index.js';
import type { CustodyConfig } from '@nestor/shared-types';

const DAY_MS = 86_400_000;

/** ISO `YYYY-MM-DD` en UTC. */
function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Vrai si `date` correspond à un jour de transition de garde
 * (changement de parent référent). Pour `weekly`, transition tous
 * les 7 jours à partir de `custody.startDate`.
 */
export function isCustodyTransition(date: Date, custody: CustodyConfig | null): boolean {
  if (!custody?.startDate) return false;
  const start = new Date(custody.startDate);
  const diffDays = Math.floor((date.getTime() - start.getTime()) / DAY_MS);
  if (custody.type === 'weekly') return diffDays % 7 === 0;
  // Phase 6 portera les autres patterns (2-2-3, 2-2-5-5, etc.).
  return false;
}

/**
 * Analyse le contexte d'un foyer co-parentage à un instant donné.
 * **Pas de side effect** — pas de mutation, pas de localStorage.
 */
export function analyzeContext(data: AiInputData, now: Date = new Date()): AiContext {
  const todayStr = toIso(now);
  const tomorrowStr = toIso(new Date(now.getTime() + DAY_MS));

  const custody = data.custody ?? null;
  const isTransitionDay = isCustodyTransition(now, custody);
  const isTransitionTomorrow = isCustodyTransition(new Date(now.getTime() + DAY_MS), custody);

  const upcomingActivities = (data.activities ?? []).filter(
    (a) => a.date >= todayStr && a.date <= tomorrowStr,
  );
  const missingItems = (data.bag ?? [])
    .filter((b: AiBagItem) => b.status === 'missing')
    .map((b) => b.item);
  const recentJournal = (data.journal ?? []).slice(0, 3);

  const stressLevel: AiContext['stressLevel'] =
    missingItems.length > 2
      ? 'high'
      : isTransitionDay && upcomingActivities.length > 1
        ? 'medium'
        : 'low';

  return {
    today: todayStr,
    isTransitionDay,
    isTransitionTomorrow,
    upcomingActivities,
    missingItems,
    recentJournal,
    stressLevel,
    custody,
  };
}

/**
 * Met à jour la mémoire des oublis à partir des items actuellement
 * `missing` dans le sac. Side effect : écriture localStorage. Retourne
 * la mémoire mise à jour pour chaînage avec `generatePredictions()`.
 *
 * Cohérent avec `aiEngine.detectPatterns()` du proto — chaque appel
 * incrémente le compteur, qui se compare au seuil de 3 dans
 * `generatePredictions` pour détecter les patterns récurrents.
 */
export function detectPatterns(data: AiInputData, currentMemory: AiMemory): AiMemory {
  const updated: AiMemory = {
    ...currentMemory,
    missedItems: { ...currentMemory.missedItems },
  };
  for (const b of data.bag ?? []) {
    if (b.status === 'missing') {
      const key = b.item.toLowerCase();
      updated.missedItems[key] = (updated.missedItems[key] ?? 0) + 1;
    }
  }
  saveMemory(updated);
  return updated;
}

/**
 * Génère les prédictions à partir du contexte et de la mémoire.
 * Filtre par `riskLevel >= 0.6`, trie par risque décroissant.
 *
 * Heuristiques couvertes :
 *   - `bag_missing` (transition demain + items missing > 0)
 *   - `sport_prep` (transition demain + activité sport)
 *   - `medical_prep` (transition demain + activité médicale)
 *   - `recurring_miss` (item oublié ≥ 3 fois en mémoire)
 *   - `high_stress` (stressLevel === high)
 */
export function generatePredictions(context: AiContext, memory: AiMemory): Prediction[] {
  const predictions: Prediction[] = [];

  if (context.isTransitionTomorrow) {
    if (context.missingItems.length > 0) {
      predictions.push({
        type: 'bag_missing',
        riskLevel: 0.95,
        items: context.missingItems,
      });
    }
    const sportAct = context.upcomingActivities.find((a) => a.type === 'sport');
    if (sportAct) {
      predictions.push({
        type: 'sport_prep',
        riskLevel: 0.8,
        activity: sportAct.title,
      });
    }
    const medAct = context.upcomingActivities.find((a) => a.type === 'medical');
    if (medAct) {
      predictions.push({
        type: 'medical_prep',
        riskLevel: 0.9,
        activity: medAct.title,
      });
    }
  }

  for (const [item, count] of Object.entries(memory.missedItems)) {
    if (count >= 3) {
      predictions.push({
        type: 'recurring_miss',
        riskLevel: Math.min(0.95, 0.5 + count * 0.1),
        item,
      });
    }
  }

  if (context.stressLevel === 'high') {
    predictions.push({ type: 'high_stress', riskLevel: 0.7 });
  }

  return predictions.filter((p) => p.riskLevel >= 0.6).sort((a, b) => b.riskLevel - a.riskLevel);
}

/**
 * Score de situation 0..1. Formule fidèle au proto :
 *   - 40 % : ratio d'items du sac en statut `ok`
 *   - 30 % : ratio d'activités à venir avec parent référent assigné
 *   - 30 % : bonus si le sac existe (encourage la création initiale)
 *
 * Retourne 1 quand il n'y a rien à organiser (pas de stress sans data).
 */
export function getSituationScore(data: AiInputData, now: Date = new Date()): number {
  const bag = data.bag ?? [];
  const activities = data.activities ?? [];
  const today = toIso(now);
  const upcoming = activities.filter((a) => a.date >= today);

  const bagOk = bag.length ? bag.filter((b) => b.status === 'ok').length / bag.length : 1;
  const actsPrepped = upcoming.length
    ? upcoming.filter((a) => a.parent !== undefined).length / upcoming.length
    : 1;
  const score = bagOk * 0.4 + actsPrepped * 0.3 + (bag.length > 0 ? 0.3 : 0);
  return Math.min(1, score);
}

/**
 * Libellé bienveillant pour un score de situation. Toujours formulé
 * positivement (CLAUDE.md §2.1).
 */
export function getSituationLabel(score: number): string {
  if (score >= 0.85) return t('ai_score_excellent');
  if (score >= 0.6) return t('ai_score_good');
  return t('ai_score_low');
}

/**
 * Résumé hebdomadaire compact, séparé par ` · `. Énumère les éléments
 * positifs (activités planifiées, journal entries) puis ajoute un
 * libellé d'organisation (bonne / à suivre).
 */
export function generateWeeklySummary(data: AiInputData, now: Date = new Date()): string {
  const acts = (data.activities ?? []).length;
  const journalCount = (data.journal ?? []).length;
  const score = getSituationScore(data, now);
  const lines: string[] = [];

  if (acts > 0) {
    lines.push(
      acts > 1
        ? interpolate(t('ai_summary_activities_many'), { count: acts.toString() })
        : t('ai_summary_activities_one'),
    );
  }
  if (journalCount > 0) {
    lines.push(
      journalCount > 1
        ? interpolate(t('ai_summary_journal_many'), { count: journalCount.toString() })
        : t('ai_summary_journal_one'),
    );
  }
  lines.push(score >= 0.8 ? t('ai_summary_organized') : t('ai_summary_to_follow'));

  return lines.join(' · ');
}

/**
 * Substitution `{key}` → `vars[key]` dans un template i18n. Helper
 * interne — exporté pour les autres modules du AI engine.
 */
export function interpolate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? `{${k}}`);
}
